import React from 'react';
import type { CardData, AddressData } from '../steps';
import { requestRecaptchaV3Token } from '../../Wrappers/ReCaptchaV3';
import { useDonorContext } from '../../../providers/DonorProvider/DonorProvider';
import { useDonorLogin } from '../../../providers/DonorProvider/useDonorLogin';
import { ApiHandler, ErrorResponse, useDonorProfile } from '../../../api';
import { usePushEvents } from '../../../hooks/PushEvents';
import {
    joinSuccessful,
    loginPopupLoginScreen,
    loginPopupSignupScreen,
    loginPopupSuccessfulSignupAfterFailure,
} from '../../../hooks/PushEvents/clevertapEvents';
import { useLoginToastContext } from '../../../providers/LoginToastProvider';
import { setLoginToken } from '../../../providers/DonorProvider/donorLocalStorage';
import { isPPOError } from '../../../utils/cardUtils';
import { usePaymentOutageContext } from '../../../providers/PaymentOutageProvider';
import {
    removeQueryString,
    useQueryString,
} from '../../../hooks/useQueryString';

export type AuthStep =
    | 'email'
    | 'login'
    | 'name'
    | 'card'
    | 'card2'
    | 'address'
    | 'address2'
    | 'password'
    | 'recovery';

export const useAuthModal = (
    donation: string | undefined,
    setOpen: (open: boolean) => unknown,
) => {
    const query = useQueryString();
    const emailInQuery = query.get('login')?.replace(/ /g, '+');
    const [step, setStep] = React.useState<AuthStep>(
        emailInQuery ? 'login' : 'email',
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const [isNewUser, setIsNewUser] = React.useState(true);
    const [formData, setFormData] = React.useState<{
        email: string;
        name: string;
        card: CardData | undefined;
        address: AddressData | undefined;
    }>({
        email: emailInQuery ? emailInQuery : '',
        name: '',
        card: undefined,
        address: undefined,
    });
    const {
        email: savedEmail,
        name: savedName,
        card: savedCard,
        address: savedAddress,
    } = formData;
    const [stepError, setStepError] = React.useState<string | null>(null);
    const [lastCardStep, setLastCardStep] = React.useState<'card' | 'card2'>(
        'card',
    );
    const [isAlreadySignedIn, setIsAlreadySignedIn] = React.useState(false);
    const [recoverySuccess, setRecoverySuccess] = React.useState(false);

    const { setDonorContext, loadDonor } = useDonorContext();
    const { donorProfile: donor } = useDonorProfile();
    const { emailLogin } = useDonorLogin();
    const { handleOpenLoginToast } = useLoginToastContext();
    const { handleOpen } = usePaymentOutageContext();
    const { pushEvent } = usePushEvents();

    const skipCreditCard = !donation;

    const dismissError = React.useCallback(() => setStepError(null), []);

    const resetModal = React.useCallback(() => {
        setStep('email');
        setFormData({
            email: '',
            name: '',
            card: undefined,
            address: undefined,
        });
        setStepError(null);
        setIsNewUser(true);
        setLastCardStep('card');
        setIsAlreadySignedIn(false);
        setRecoverySuccess(false);
        removeQueryString('login');
    }, []);

    const resetModalAndClose = React.useCallback(() => {
        resetModal();
        setOpen(false);
    }, [resetModal, setOpen]);

    const onEmailSubmit = React.useCallback(
        async (email: string) => {
            setFormData((prev) => ({ ...prev, email }));
            setStepError(null);
            setIsLoading(true);
            requestRecaptchaV3Token(
                async (token: string | undefined) => {
                    setDonorContext((s) => ({
                        ...s,
                        loginResponse: {
                            ...s.loginResponse,
                            error: null,
                        },
                    }));
                    const apiRequest =
                        await ApiHandler.instance.auth.getLoginTypes(
                            email,
                            token,
                        );
                    if (apiRequest.success) {
                        const isUserNotFound =
                            apiRequest.data.response.payload.loginTypes
                                .length === 0;
                        pushEvent(
                            isUserNotFound
                                ? loginPopupSignupScreen
                                : loginPopupLoginScreen,
                            { email },
                        );
                        if (isUserNotFound) {
                            setIsNewUser(true);
                            setStep('name');
                        } else {
                            setIsNewUser(false);
                            setStep('login');
                        }
                    } else {
                        setStepError(apiRequest.error.message);
                    }
                    setIsLoading(false);
                },
                undefined,
                'LoginType',
            );
        },
        [setDonorContext, pushEvent],
    );

    const onNameSubmit = React.useCallback(
        (name: string) => {
            setFormData((prev) => ({ ...prev, name }));
            setStepError(null);
            setStep(skipCreditCard ? 'password' : 'card');
        },
        [skipCreditCard],
    );

    const onCardSubmit = React.useCallback(
        (card: CardData) => {
            setFormData((prev) => ({ ...prev, card }));
            setStepError(null);
            setLastCardStep(step as 'card' | 'card2');
            setStep('address');
        },
        [step],
    );

    const submitCard = React.useCallback(
        async (token: string | undefined, address: AddressData | undefined) => {
            const walletRequest = await ApiHandler.instance.gift.addCardRequest(
                {
                    name: savedName,
                    email: savedEmail,
                    address,
                    card: savedCard,
                    skipsCreditCard: false,
                    skipsSignup: true,
                },
                token,
            );
            if (walletRequest.success || isPPOError(walletRequest.error)) {
                loadDonor(true);
                isPPOError(walletRequest.error) && handleOpen(true);
                if (isAlreadySignedIn) {
                    pushEvent(loginPopupSuccessfulSignupAfterFailure);
                }
                resetModalAndClose();
                return true;
            } else {
                setStepError(
                    'We were unable to validate your card. Please ensure that you are entering all the details correctly.',
                );
                setIsAlreadySignedIn(true);
                setStep('card2');
                return false;
            }
        },
        [
            handleOpen,
            isAlreadySignedIn,
            loadDonor,
            pushEvent,
            resetModalAndClose,
            savedCard,
            savedEmail,
            savedName,
        ],
    );

    const onAddress2Submit = React.useCallback(
        (city: string, state: string, baseAddress?: AddressData) => {
            const base = baseAddress ?? savedAddress;
            const mergedAddress: AddressData = base
                ? { ...base, city, state }
                : { street: '', zip: '', countryCode: 'US', city, state };
            setFormData((prev) => ({ ...prev, address: mergedAddress }));
            setStepError(null);
            if (isAlreadySignedIn) {
                setIsLoading(true);
                requestRecaptchaV3Token(
                    async (token: string | undefined) => {
                        const closed = await submitCard(token, mergedAddress);
                        if (closed) return;
                        setIsLoading(false);
                    },
                    undefined,
                    'AddressComplete',
                );
            } else {
                setStep('password');
            }
        },
        [isAlreadySignedIn, savedAddress, submitCard],
    );

    const onAddressSubmit = React.useCallback(
        (address: AddressData) => {
            setFormData((prev) => ({ ...prev, address }));
            setStepError(null);
            if (address.city && address.state) {
                onAddress2Submit(address.city, address.state, address);
            } else {
                setStep('address2');
            }
        },
        [onAddress2Submit],
    );

    const onPasswordSubmit = React.useCallback(
        async (password: string) => {
            setIsLoading(true);
            setDonorContext((s) => ({
                ...s,
                loginResponse: {
                    data: null,
                    error: null,
                    loading: true,
                },
            }));
            setStepError(null);
            requestRecaptchaV3Token(
                async (token: string | undefined) => {
                    const request = await ApiHandler.instance.auth.userSignup(
                        {
                            email: savedEmail,
                            name: savedName,
                        },
                        password,
                        token,
                    );
                    let closed = false;
                    if (request.success) {
                        handleOpenLoginToast();
                        pushEvent(joinSuccessful, { type: 'Email' });
                        setLoginToken(request.data.response.payload);
                        if (skipCreditCard) {
                            loadDonor(true);
                        } else {
                            closed = await submitCard(token, savedAddress);
                        }
                    } else {
                        setStepError(request.error.message);
                    }
                    if (closed) return;
                    setDonorContext((s) => ({
                        ...s,
                        loginResponse: {
                            data: request.success
                                ? request.data.response.payload
                                : null,
                            error: !request.success ? request.error : null,
                            loading: false,
                        },
                    }));
                    setIsLoading(false);
                },
                undefined,
                'Password',
            );
        },
        [
            setDonorContext,
            savedEmail,
            savedName,
            handleOpenLoginToast,
            pushEvent,
            skipCreditCard,
            loadDonor,
            savedAddress,
            submitCard,
        ],
    );

    const onRecoverySubmit = React.useCallback(async (email: string) => {
        setIsLoading(true);
        setStepError(null);
        const resetPassword =
            await ApiHandler.instance.auth.requestPasswordReset(email);
        if (resetPassword.success) {
            setRecoverySuccess(true);
            setStep('login');
        } else {
            setStepError(
                resetPassword.error.message ||
                    'Something went wrong. Please try again.',
            );
        }
        setIsLoading(false);
    }, []);

    const onAlreadyHaveAccount = React.useCallback(() => {
        setStepError(null);
        setStep('login');
    }, []);

    const onForgotPassword = React.useCallback(() => {
        setStepError(null);
        setRecoverySuccess(false);
        setStep('recovery');
    }, []);

    const onBack = React.useMemo(() => {
        if (step === 'email') return undefined;
        if (step === 'login') {
            if (emailInQuery) return undefined;
            return () => setStep('email');
        }
        if (step === 'recovery')
            return () => {
                setStepError(null);
                setStep('login');
            };
        if (step === 'name') return () => setStep('email');
        if (step === 'card') return () => setStep('name');
        if (step === 'card2') return undefined;
        if (step === 'address') return () => setStep(lastCardStep);
        if (step === 'address2') return () => setStep('address');
        if (step === 'password') {
            if (!isNewUser) return () => setStep('email');
            return () => setStep(skipCreditCard ? 'name' : 'address2');
        }
        return undefined;
    }, [step, emailInQuery, lastCardStep, isNewUser, skipCreditCard]);

    const handleClose = React.useCallback(() => {
        if (!isLoading) {
            if (isAlreadySignedIn) {
                loadDonor(true);
                handleOpen(true);
            }
            resetModalAndClose();
        }
    }, [
        handleOpen,
        isAlreadySignedIn,
        isLoading,
        loadDonor,
        resetModalAndClose,
    ]);

    const onLoginSubmit = React.useCallback(
        async (password: string) => {
            setIsLoading(true);
            setStepError(null);
            await emailLogin(
                savedEmail,
                password,
                () => {
                    setIsLoading(false);
                    resetModalAndClose();
                },
                (error: ErrorResponse) => {
                    setIsLoading(false);
                    setStepError(error.message);
                },
            );
        },
        [emailLogin, savedEmail, resetModalAndClose],
    );

    React.useEffect(() => {
        // TODO: look into this logic
        if (donor) {
            handleClose();
        }
    }, [donor, handleClose]);

    return {
        step,
        isLoading,
        savedEmail,
        savedName,
        savedCard,
        savedAddress,
        skipCreditCard,
        stepError,
        dismissError,
        onEmailSubmit,
        onNameSubmit,
        onCardSubmit,
        onAddressSubmit,
        onAddress2Submit,
        onLoginSubmit,
        onPasswordSubmit,
        onAlreadyHaveAccount,
        onForgotPassword,
        onRecoverySubmit,
        recoverySuccess,
        onBack,
        resetModal,
        handleClose,
    };
};
